/**
 * Tool System for Canvas Editor
 *
 * Modal tool system that supports:
 * - One-shot tools (create then exit)
 * - Locked tools (stay in mode until manually exited)
 * - Extensible tool registry
 * - Keyboard shortcuts for tool activation
 * - UI binding via callbacks
 */

import type { InputSystem, InputContext } from "@rool/input";

// =============================================================================
// Types
// =============================================================================

/** Unique identifier for a tool */
export type ToolId = string;

/** Definition of a tool */
export interface NodeTool {
  /** Unique identifier (used in registry and modes) */
  id: ToolId;

  /** Node type to create (passed to node factory). Set to null for non-creation tools like 'connect'. */
  nodeType: string | null;

  /** Human-readable label for UI */
  label: string;

  /** Optional keyboard shortcut (e.g., 'n', 't', 's') */
  shortcut?: string;

  /** Optional icon identifier for toolbar */
  icon?: string;

  /** Optional SVG data URI for custom cursor when tool is active */
  cursorSvg?: string;
}

/** Current tool state */
export interface ToolState {
  /** Currently active tool (null = default/select mode) */
  activeTool: ToolId | null;

  /** Whether the tool is locked (stays active after use) */
  locked: boolean;
}

/** Callback when tool state changes */
export type ToolStateChangeCallback = (state: ToolState) => void;

// =============================================================================
// Initial State
// =============================================================================

/** Create initial tool state */
export function createInitialToolState(): ToolState {
  return {
    activeTool: null,
    locked: false,
  };
}

// =============================================================================
// State Transitions (Pure Functions)
// =============================================================================

/**
 * Activate a tool.
 * @param state - Current tool state
 * @param toolId - Tool to activate
 * @param locked - Whether to lock the tool
 */
export function activateTool(
  state: ToolState,
  toolId: ToolId,
  locked: boolean = false,
): ToolState {
  return {
    activeTool: toolId,
    locked,
  };
}

/**
 * Deactivate the current tool, returning to default mode.
 */
export function deactivateTool(state: ToolState): ToolState {
  if (state.activeTool === null) {
    return state; // Already deactivated
  }
  return {
    activeTool: null,
    locked: false,
  };
}

/**
 * Called after tool is used. Deactivates unless locked.
 */
export function toolUsed(state: ToolState): ToolState {
  if (state.locked) {
    return state; // Stay in tool mode
  }
  return deactivateTool(state);
}

/**
 * Toggle lock state for current tool.
 */
export function toggleLock(state: ToolState): ToolState {
  if (state.activeTool === null) {
    return state; // No tool to lock
  }
  return {
    ...state,
    locked: !state.locked,
  };
}

/**
 * Check if a specific tool is active.
 */
export function isToolActive(state: ToolState, toolId: ToolId): boolean {
  return state.activeTool === toolId;
}

/**
 * Check if any tool is active.
 */
export function hasActiveTool(state: ToolState): boolean {
  return state.activeTool !== null;
}

// =============================================================================
// Tool Registry
// =============================================================================

export interface ToolRegistry {
  /** Register a new tool */
  register(tool: NodeTool): void;

  /** Get tool by ID */
  get(id: ToolId): NodeTool | undefined;

  /** Get tool by shortcut key */
  getByShortcut(key: string): NodeTool | undefined;

  /** Get all registered tools */
  getAll(): NodeTool[];

  /** Check if a tool is registered */
  has(id: ToolId): boolean;
}

/**
 * Create a tool registry.
 */
export function createToolRegistry(): ToolRegistry {
  const tools = new Map<ToolId, NodeTool>();
  const shortcutIndex = new Map<string, ToolId>();

  return {
    register(tool: NodeTool): void {
      if (tools.has(tool.id)) {
        console.warn(
          `[tools] Tool "${tool.id}" already registered, overwriting`,
        );
      }
      tools.set(tool.id, tool);

      if (tool.shortcut) {
        if (shortcutIndex.has(tool.shortcut.toLowerCase())) {
          console.warn(
            `[tools] Shortcut "${tool.shortcut}" already mapped, overwriting`,
          );
        }
        shortcutIndex.set(tool.shortcut.toLowerCase(), tool.id);
      }
    },

    get(id: ToolId): NodeTool | undefined {
      return tools.get(id);
    },

    getByShortcut(key: string): NodeTool | undefined {
      const toolId = shortcutIndex.get(key.toLowerCase());
      return toolId ? tools.get(toolId) : undefined;
    },

    getAll(): NodeTool[] {
      return Array.from(tools.values());
    },

    has(id: ToolId): boolean {
      return tools.has(id);
    },
  };
}

// =============================================================================
// Default Tools
// =============================================================================

// SVG cursor data URIs for creation tools — matches Phosphor icons used in toolbar
// NoteBlank (Phosphor "NoteBlank" regular weight, viewBox 0 0 256 256, filled)
const CURSOR_STICKY_NOTE = `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 256 256' fill='%23000'><path d='M208,32H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H156.69A15.86,15.86,0,0,0,168,219.31L219.31,168A15.86,15.86,0,0,0,224,156.69V48A16,16,0,0,0,208,32ZM48,48H208V152H160a8,8,0,0,0-8,8v48H48ZM196.69,168,168,196.69V168Z'/></svg>") 12 12, crosshair`;
// TextT (Phosphor "TextT" regular weight)
const CURSOR_HEADER = `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 256 256' fill='%23000'><path d='M208,56V88a8,8,0,0,1-16,0V64H136V192h24a8,8,0,0,1,0,16H96a8,8,0,0,1,0-16h24V64H64V88a8,8,0,0,1-16,0V56a8,8,0,0,1,8-8H200A8,8,0,0,1,208,56Z'/></svg>") 12 12, crosshair`;
// ArticleMedium (Phosphor "ArticleMedium" regular weight)
const CURSOR_MARKDOWN = `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 256 256' fill='%23000'><path d='M56,136a8,8,0,0,1-8,8H24a8,8,0,0,1,0-16h8V64H24a8,8,0,0,1,0-16H40v0a8,8,0,0,1,6.78,3.74L80,104.91l33.22-53.15A8,8,0,0,1,120,48v0h16a8,8,0,0,1,0,16h-8v64h8a8,8,0,0,1,0,16H112a8,8,0,0,1,0-16V83.89L86.78,124.24a8,8,0,0,1-13.56,0L48,83.89V128A8,8,0,0,1,56,136Zm112-24h64a8,8,0,0,0,0-16H168a8,8,0,0,0,0,16Zm64,16H168a8,8,0,0,0,0,16h64a8,8,0,0,0,0-16Zm0,32H80a8,8,0,0,0,0,16H232a8,8,0,0,0,0-16Zm0,32H80a8,8,0,0,0,0,16H232a8,8,0,0,0,0-16Z'/></svg>") 12 12, crosshair`;

/** Default tools */
export const DEFAULT_TOOLS: NodeTool[] = [
  { id: "select", nodeType: null, label: "Select", icon: "cursor" },
  {
    id: "sticky-note",
    nodeType: "sticky-note",
    label: "Sticky Note",
    shortcut: "n",
    icon: "note",
    cursorSvg: CURSOR_STICKY_NOTE,
  },
  {
    id: "header",
    nodeType: "header",
    label: "Header",
    shortcut: "h",
    icon: "heading",
    cursorSvg: CURSOR_HEADER,
  },
  {
    id: "markdown",
    nodeType: "markdown",
    label: "Markdown",
    shortcut: "m",
    icon: "text",
    cursorSvg: CURSOR_MARKDOWN,
  },
  {
    id: "connect",
    nodeType: null,
    label: "Connect",
    shortcut: "c",
    icon: "vector-line",
  },
];

// =============================================================================
// Tool Manager
// =============================================================================

export interface ToolManagerOptions {
  /** Callback when tool state changes */
  onToolChange?: ToolStateChangeCallback;

  /** Callback to create a node (injected by harness/editor) */
  createNode: (nodeType: string, position: { x: number; y: number }) => void;

  /** Function to get current cursor position in world coordinates */
  getCursorPosition: () => { x: number; y: number };

  /** Input system for mode management */
  input: InputSystem;

  /** Initial tools to register (defaults to DEFAULT_TOOLS) */
  tools?: NodeTool[];
}

export interface ToolManager {
  /** Get current tool state */
  getState(): ToolState;

  /** Get tool registry */
  getRegistry(): ToolRegistry;

  /** Programmatically activate a tool */
  activate(toolId: ToolId, locked?: boolean): void;

  /** Deactivate current tool */
  deactivate(): void;

  /** Toggle lock on current tool */
  toggleLock(): void;

  /** Handle toolbar button click (with shift detection for lock) */
  handleToolbarClick(toolId: ToolId, shiftKey: boolean): void;

  /**
   * Notify that the current tool was used.
   * Called by behaviors (e.g., rotate) after completing their action.
   * Auto-exits tool mode unless the tool is locked.
   */
  notifyToolUsed(): void;

  /** Cleanup */
  destroy(): void;
}

/** Mode name for active tool */
const TOOL_MODE = "tool";

/**
 * Create a tool manager that coordinates tool state with input system.
 */
export function createToolManager(options: ToolManagerOptions): ToolManager {
  const {
    onToolChange,
    createNode,
    getCursorPosition,
    input,
    tools = DEFAULT_TOOLS,
  } = options;

  // State
  let state = createInitialToolState();
  const registry = createToolRegistry();
  const unsubscribers: (() => void)[] = [];

  // Register provided tools
  for (const tool of tools) {
    registry.register(tool);
  }

  // Helper to update state and notify
  function updateState(newState: ToolState): void {
    const prevState = state;
    state = newState;

    // Handle mode transitions
    if (prevState.activeTool === null && newState.activeTool !== null) {
      // Entering tool mode
      input.pushMode(TOOL_MODE);
    } else if (prevState.activeTool !== null && newState.activeTool === null) {
      // Exiting tool mode
      input.popMode(TOOL_MODE);
    }

    onToolChange?.(state);
  }

  // Register keyboard shortcuts for tool activation (in default mode)
  // and for toggling off (in tool mode)
  for (const tool of registry.getAll()) {
    if (tool.shortcut) {
      // Handler for default mode - activates or toggles tool
      unsubscribers.push(
        input.on(
          "keydown",
          tool.shortcut,
          { mode: "default", command: `${tool.label} Tool`, category: "Tools" },
          () => {
            // Toggle off if already active, otherwise activate
            if (state.activeTool === tool.id) {
              updateState(deactivateTool(state));
            } else {
              updateState(activateTool(state, tool.id, false));
            }
          },
        ),
      );

      // Handler for tool mode - toggle off if same tool, switch if different
      unsubscribers.push(
        input.on(
          "keydown",
          tool.shortcut,
          {
            mode: TOOL_MODE,
            command: `Toggle ${tool.label} Tool`,
            category: "Tools",
          },
          () => {
            if (state.activeTool === tool.id) {
              updateState(deactivateTool(state));
            } else {
              // Switch to different tool
              updateState(activateTool(state, tool.id, false));
            }
          },
        ),
      );
    }
  }

  // Register click handler for tool mode (creates node on click)
  unsubscribers.push(
    input.on("mousedown", { mode: TOOL_MODE }, (ctx: InputContext) => {
      const tool = registry.get(state.activeTool!);
      if (tool && ctx.button === 0) {
        // Left click only
        // Only create node if tool has a nodeType (skip for connect tool, etc.)
        if (tool.nodeType) {
          const position = getCursorPosition();
          createNode(tool.nodeType, position);

          // Auto-exit unless locked
          updateState(toolUsed(state));

          ctx.stopPropagation(); // Don't trigger default mode handlers
        }
        // For tools without nodeType (like connect), let other handlers process the event
      }
    }),
  );

  // Escape exits tool mode
  unsubscribers.push(
    input.on(
      "keydown",
      "escape",
      { mode: TOOL_MODE, command: "Exit Tool Mode", category: "Tools" },
      () => {
        updateState(deactivateTool(state));
      },
    ),
  );

  const manager: ToolManager = {
    getState(): ToolState {
      return state;
    },

    getRegistry(): ToolRegistry {
      return registry;
    },

    activate(toolId: ToolId, locked: boolean = false): void {
      if (!registry.has(toolId)) {
        console.warn(`[tools] Unknown tool: ${toolId}`);
        return;
      }
      updateState(activateTool(state, toolId, locked));
    },

    deactivate(): void {
      updateState(deactivateTool(state));
    },

    toggleLock(): void {
      updateState(toggleLock(state));
    },

    handleToolbarClick(toolId: ToolId, shiftKey: boolean): void {
      if (!registry.has(toolId)) {
        console.warn(`[tools] Unknown tool: ${toolId}`);
        return;
      }

      // If clicking the already-active tool without shift, deactivate it
      if (state.activeTool === toolId && !shiftKey) {
        updateState(deactivateTool(state));
        return;
      }

      // If shift is held, toggle lock on current tool if same tool
      if (state.activeTool === toolId && shiftKey) {
        updateState(toggleLock(state));
        return;
      }

      // Activate with lock if shift is held
      updateState(activateTool(state, toolId, shiftKey));
    },

    notifyToolUsed(): void {
      // Auto-exit tool mode unless locked
      updateState(toolUsed(state));
    },

    destroy(): void {
      // Cleanup handlers
      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
      unsubscribers.length = 0;

      // Exit tool mode if active
      if (state.activeTool !== null) {
        input.popMode(TOOL_MODE);
      }
    },
  };

  return manager;
}
